import React, { useState } from 'react';
import I18n from 'i18n-js';
import { useQuery } from '@tanstack/react-query';
// AIDEV-NOTE: Not used here. ReactRailsUJS bundles every component, so this loads the sheet's global rules
// (tippy, video.js, .border-*, ReactModal) app-wide; audit them before removing it with the dependency.
import '@tci/media-libraries/dist/style.css';
import Text from '@/components/ui/Text';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import QueryKeys from '@/utils/QueryKeys';
import cn from '@/utils/cn';
import { fetchReport } from './actions';
import ExportCsvLink from './ExportCsvLink';
import Loading from './Loading';
import PerformanceModal from './PerformanceModal';
import RangeLegend from './RangeLegend';
import ScoreTable from './ScoreTable';
import { FrameworkNoun, SelectedScore } from './types';

interface BenchmarksProps {
  addStudentsPath: string;
  classroomId: number;
  frameworkNoun: FrameworkNoun;
  programId: number;
}

const BenchmarksReport = ({ addStudentsPath, classroomId, frameworkNoun, programId }: BenchmarksProps) => {
  const [selected, setSelected] = useState<SelectedScore | null>(null);
  const { data, isError } = useQuery([QueryKeys.StandardsReport, classroomId], () => fetchReport(classroomId));

  if (isError) return <Text className="tw-p-4 tw-text-center">{I18n.t('standards_load_error')}</Text>;
  if (!data) return <Loading className="tw-my-60" size={80} />;

  const { students, tags } = data;

  if (tags.length === 0) {
    return (
      <Text className="tw-p-4 tw-text-center">
        {I18n.t(frameworkNoun === 'Benchmarks' ? 'standards_report_no_benchmarks' : 'standards_report_no_standards')}
      </Text>
    );
  }

  const hasStudents = students.length > 0;

  return (
    <>
      <div className="tw-flex tw-justify-end tw-gap-3 tw-mb-1">
        <RangeLegend />
        <ExportCsvLink frameworkNoun={frameworkNoun} students={students} tags={tags} />
      </div>

      <div
        className={cn(hasStudents
          ? 'tw-overflow-auto tw-h-[calc(100vh-216px)]'
          : 'tw-overflow-hidden tw-border tw-border-solid tw-border-light-grey')}
      >
        <ScoreTable students={students} tags={tags} onSelectScore={setSelected} />
        {!hasStudents && (
          <div
            className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-p-4
              tw-border-0 tw-border-t tw-border-solid tw-border-light-grey"
          >
            <Text className="tw-m-0">{I18n.t('standards_report_no_students')}</Text>
            <a className="btn btn--green" href={addStudentsPath}>{I18n.t('standards_report_add_students')}</a>
          </div>
        )}
      </div>

      <PerformanceModal
        classroomId={classroomId}
        frameworkNoun={frameworkNoun}
        programId={programId}
        selected={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

const Benchmarks = (props: BenchmarksProps) => (
  <ReactQueryProvider>
    <BenchmarksReport {...props} />
  </ReactQueryProvider>
);

export default Benchmarks;
